import React, {
  createRef,
  useContext,
  useState,
  useCallback,
  useEffect
} from 'react';
import {
  getTranslatedText,
  withErrorBoundary,
  useIntl,
  preFormatForm,
  validateForm,
  shareTvUrlConstraints,
  AppContext,
  isAuthorised
} from '@nile/components';
import RGL from 'react-grid-layout';
import { SizeMe } from 'react-sizeme';
import { SUMMARY_TABLE } from '@nile/reports/src/modules/charts/constants';
import {
  TVMODE_INTERVAL,
  SHARETV_EXPIRY_PERIOD
} from '@nile/reports/src/modules/reports/constants';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import { cloneDeep, isEmpty, omit } from 'lodash';
import ChartHome from '@nile/reports/src/modules/charts/components/pages/ChartHome';
import GridHeader from '@nile/reports/src/modules/reports/components/subpages/NewReportGrid/GridHeader';
import RenderChart from '@nile/reports/src/modules/reports/components/subpages/RenderChart';
import { BuilderWrapper, Layout, El } from './styles';
import {
  Button,
  Dropdown,
  Card,
  Heading,
  Modal,
  TextStyle,
  Tippy,
  TextField
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  UPDATE_REPORT,
  CREATE_DASHBOARD_SHARE_LINK
} from '@nile/reports/src/modules/reports/graphql';
import ChartLibrary from '../ChartLibrary';
import { useMutation } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import { FormattedMessage } from 'react-intl';
import DataTable from '../../../../charts/components/subpages/DataTable';
import { ChartContextProvider } from '../../../../charts/contexts/Chart';

const ResponsiveReactGridLayout = RGL;

const layoutRef = createRef();

const ShareTvUrlModal = (props) => {
  const [active, setActive] = useState(false);
  const [copied, setCopyState] = useState(false);
  const [expiryPeriod, setExpiryPeriod] = useState({
    unit: 'DAY',
    quantity: '1'
  });
  const [shareUrl, setShareUrl] = useState('');
  const [errors, setErrors] = useState({});

  const intl = useIntl();
  const shareTvExpiryPeriod = [
    {
      label: getTranslatedText(intl, 'admin.customerSupportAccess.hours'),
      value: 'HOUR'
    },
    {
      label: getTranslatedText(intl, 'admin.customerSupportAccess.days'),
      value: 'DAY'
    }
  ];

  const resetState = () => {
    setExpiryPeriod({
      unit: 'DAY',
      quantity: '1'
    });
    setShareUrl('');
    setErrors({});
  };
  const copySubDomain = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState((prev) => !prev);
      setTimeout(() => {
        setCopyState((prev) => !prev);
      }, 500);
    }
  };

  const handleChangeExpiry = useCallback((value, type) => {
    //reset shareUrl on expiry input field change
    setShareUrl('');

    if (type === 'quantity') {
      value = value.replace(/[^0-9]/g, '');
      // limit to 180 max
      if (value) {
        const num = parseInt(value, 10);
        if (num > 180) {
          value = '180';
        }
      }
      setExpiryPeriod((prev) => ({
        ...prev,
        [type]: value
      }));
    } else {
      setExpiryPeriod((prev) => {
        if (value.value === 'HOUR' && prev.quantity > 24) {
          return {
            ...prev,
            quantity: '24'
          };
        }
        return {
          ...prev,
          [type]: value.value
        };
      });
    }
  }, []);

  const [createDashboardShareLink, { loading }] = useMutation(
    CREATE_DASHBOARD_SHARE_LINK,
    {
      client: AccountClient,
      onCompleted({ createDashboardShareLink }) {
        setShareUrl(createDashboardShareLink);
      }
    }
  );

  const handleGenerateUrl = () => {
    const error = validateForm(
      preFormatForm({
        expiryQty: Number(expiryPeriod.quantity)
      }),
      shareTvUrlConstraints
    );
    if (error) {
      setErrors(error);
      return;
    }
    //query trigger and update the shareUrl
    createDashboardShareLink({
      variables: {
        input: {
          report: {
            reportId: props?.reportId
          },
          expiryAfter: expiryPeriod
        }
      }
    });
    setErrors({});
  };
  const handleChange = useCallback(() => {
    setActive(!active);
    resetState();
  }, [active]);

  const activator = (
    <Button
      plain
      onClick={handleChange}
      icon={<SVGIcon name="Share" color="var(--sops-interactive)" />}
    >
      <FormattedMessage id="reports.dashboard.shareOnTv.label" />
    </Button>
  );

  return (
    <Modal
      activator={activator}
      open={active}
      onClose={handleChange}
      title={<FormattedMessage id="reports.dashboard.shareToTv.modal.label" />}
      large
    >
      <div className="p20">
        <div className="flex actr mb10">
          <div className="bg-pale rounded-4px p4 mr-10">
            <TextStyle variation="Medium" uppercase>
              <FormattedMessage id="admin.integrations.webroot.config.step1"></FormattedMessage>
            </TextStyle>
          </div>
          <TextStyle variation="Medium">
            <FormattedMessage id="reports.dashboard.shareToTv.modal.step1"></FormattedMessage>
          </TextStyle>
        </div>
        <div className="flex mb5">
          <TextStyle variation="Medium">
            <FormattedMessage id="reports.dashboard.shareToTv.expiry.label"></FormattedMessage>
            <span className="mandatory-star">*</span>
          </TextStyle>
        </div>
        <div className="flex actr mb20">
          <TextField
            value={expiryPeriod.quantity}
            onChange={(value) => handleChangeExpiry(value, 'quantity')}
            type="number"
            max={expiryPeriod?.unit === 'DAY' ? 180 : 24}
            min={1}
            size="small"
            error={errors?.expiryQty?.[0]}
            suffix={
              <Dropdown
                className="w100p p2 expiryunit"
                noBorder
                classNamePrefix="value_selector"
                options={shareTvExpiryPeriod}
                defaultValue={shareTvExpiryPeriod.find(
                  (option) => option.value === expiryPeriod.unit
                )}
                value={shareTvExpiryPeriod.find(
                  (option) => option.value === expiryPeriod.unit
                )}
                onChange={(e) => handleChangeExpiry(e, 'unit')}
              />
            }
          />
        </div>
        <div className="flex actr mb10">
          <div className="bg-pale rounded-4px p4 mr-10">
            <TextStyle variation="Medium" uppercase>
              <FormattedMessage id="admin.integrations.webroot.config.step2" />
            </TextStyle>
          </div>
          <TextStyle variation="Medium">
            <FormattedMessage id="reports.dashboard.shareToTv.modal.step2"></FormattedMessage>
          </TextStyle>
        </div>
        <div className="p20 mb50">
          <Card variant="secondaryCard" noShadow>
            <div className="fx actr p10 g5">
              {shareUrl && (
                <div className="overflow-ellipses font-weight-600">
                  {shareUrl}
                </div>
              )}
              <div className="fx mlauto">
                {shareUrl ? (
                  <Tippy interactive title="Copied" open={copied}>
                    <Button
                      icon={<SVGIcon name="Clone" color="#091EF5" />}
                      textAlign="right"
                      isRounded
                      onClick={copySubDomain}
                    >
                      {getTranslatedText(intl, 'common.copyurl')}
                    </Button>
                  </Tippy>
                ) : (
                  <Button
                    icon={<SVGIcon name="DownArrowCircle" color="#091EF5" />}
                    textAlign="right"
                    isRounded
                    loading={loading}
                    onClick={handleGenerateUrl}
                  >
                    {getTranslatedText(intl, 'common.generateUrl')}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Modal>
  );
};

function NewReportGrid(props) {
  const { layout, dispatch, isExport, isViewOnly, allowDragDrop } = props;
  const intl = useIntl();

  const appContext = useContext(AppContext);
  const { user } = appContext;
  const roleFeatures = user?.role?.roleFeatureList || [];
  const isShareReportAuthorised = isAuthorised(
    roleFeatures,
    'shareReport.allow'
  );

  const reportsContext = useContext(ReportsContext);
  const [tvRefreshInterval, setTvRefreshInterval] = useState(
    reportsContext?.tvRefreshRate
  );

  useEffect(() => {
    setTvRefreshInterval(reportsContext?.tvRefreshRate);
  }, [reportsContext?.tvRefreshRate]);

  const [updateReport] = useMutation(UPDATE_REPORT, {
    client: AccountClient,
    onCompleted({ updateReport }) {
      reportsContext?.updateReportDataContext(updateReport);
    }
  });
  const data = reportsContext.activeReport;

  const changefullScreenRefreshInterval = (selectedInput) => {
    // setRefreshRatePopActive(!refreshRatePopActive);
    setTvRefreshInterval(selectedInput.value);
    updateReport({
      variables: {
        reportId: data?.reportId,
        input: {
          fullScreenModeRefreshInterval: selectedInput.value
        }
      }
    });
  };

  const refreshRateItems = TVMODE_INTERVAL?.map((item) => {
    return { ...item, onAction: () => changefullScreenRefreshInterval(item) };
  });

  const [selectedElement, setSelectedElement] = useState(null);
  const [openChart, setOpenChart] = useState(false);
  const [refreshRatePopActive, setRefreshRatePopActive] = useState(false);

  const [selectedChart, setSelectedChart] = useState({
    data: null,
    index: 0,
    originalData: null
  });

  const onLayoutChange = (newLayoutItems) => {
    if (layout.findIndex((el) => el.i === '__droppingItem__') > -1) return;
    const newLayout = layout.map((el) => {
      const elementWithUpdatedPosition = newLayoutItems.find(
        (l) => l.i === el.i
      );
      return {
        ...el,
        x: elementWithUpdatedPosition.x,
        y: elementWithUpdatedPosition.y,
        h: elementWithUpdatedPosition.h,
        w: elementWithUpdatedPosition.w
      };
    });
    dispatch({ type: 'updateLayout', layout: newLayout });
  };

  const onDrop = async (allItems, layoutItem, _event) => {
    // Only add to the list if it's new
    if (
      !data.charts.find((chart) => chart.chartId === selectedElement.chartId)
    ) {
      let updatedCharts = cloneDeep(data.charts);
      updatedCharts = [
        ...updatedCharts,
        omit(selectedElement, ['metrics', 'createdBy', 'createdTime'])
      ];
      reportsContext.setActiveReport({ ...data, charts: updatedCharts });
      dispatch({
        type: 'addNewItem',
        layout: layoutItem,
        allItems: allItems,
        chart: selectedElement || {}
      });
    }
  };

  const removeElementFromLayout = (item) => {
    const chartToRemove = !!item?.isRemove
      ? layout.filter((it) => it.chart.chartId === item.chartId)?.[0] || {}
      : item;

    if (
      layout.filter((it) => it.chart.chartId === chartToRemove.chart.chartId)
        ?.length === 1
    ) {
      const updatedCharts = cloneDeep(data.charts).filter(
        (chart) => chart.chartId !== chartToRemove.chart.chartId
      );
      reportsContext.setActiveReport({ ...data, charts: updatedCharts });
      const updatedLayout = cloneDeep(layout);
      const index = layout.findIndex((el) => el.i === chartToRemove.i);
      updatedLayout.splice(index, 1);
      dispatch({ type: 'updateLayout', layout: updatedLayout });
    }
  };

  /**
   * Method to check if data table is visible
   * @param {*} chart
   * @returns boolean
   */
  const isDataTableHidden = (chart) => {
    return chart && !chart.chartId && chart.entity === 'AssetSoftwareInventory';
  };

  return (
    <BuilderWrapper isExport={isExport} className="flex full-width full-height">
      <Layout
        className={`full-height p10 ${
          reportsContext?.isTvDashboard ? '' : 'scroll'
        }`}
        navOpen={!isExport && reportsContext.showChartsLibrary}
        ref={layoutRef}
        id="reportgridlayout"
      >
        {reportsContext?.isTvDashboardEnabled &&
          !reportsContext?.isTvDashboard && (
            <div className="fx actr jspbtwn pb10">
              <Heading element="h1">
                <div className={`flex actr wm-700`}>
                  <div className="ellipsis cursor-default">{data.name}</div>
                </div>
              </Heading>
              <div className="fx actr g10">
                <div
                  className={`${
                    reportsContext?.isReportEditAuthorised
                      ? ''
                      : 'disable-actions'
                  }`}
                >
                  <div className="fx actr action-show">
                    <div className="fx actr g2 cursor-default">
                      <TextStyle variation="subdued">
                        <FormattedMessage id="reports.dashboard.refreshRate.label" />{' '}
                        :
                      </TextStyle>
                    </div>
                    <div className="w100px">
                      <Dropdown
                        noBorder
                        value={refreshRateItems?.filter(
                          (list) => list.value === tvRefreshInterval
                        )}
                        options={refreshRateItems}
                        onMenuOpen={() => setRefreshRatePopActive(true)}
                        onMenuClose={() => setRefreshRatePopActive(false)}
                        components={{
                          DropdownIndicator: (props) => (
                            <div className="fx center mr8 action-visibility">
                              <SVGIcon
                                name={
                                  refreshRatePopActive ? 'ArrowDown' : 'Edit'
                                }
                                color={'var(--sops-black-white-bg)'}
                              />
                            </div>
                          ),
                          IndicatorSeparator: () => null
                        }}
                        label={'Select refreshrate'}
                        placeholder={'NA'}
                        onChange={(listSelected) => {
                          changefullScreenRefreshInterval(listSelected);
                        }}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            border: 'none',
                            padding: '4px',
                            '&:hover': {
                              background: '#ececec',
                              borderRadius: '4px',
                              padding: '4px',
                              border: 'none !important'
                            }
                          })
                        }}
                        isDisabled={!reportsContext?.isReportEditAuthorised}
                      />
                    </div>
                  </div>
                </div>
                {isShareReportAuthorised && (
                  <ShareTvUrlModal reportId={data.reportId} />
                )}
              </div>
            </div>
          )}
        <SizeMe monitorHeight monitorWidth>
          {({ size }) => (
            <ResponsiveReactGridLayout
              className={`layout ${layout?.length === 0 ? 'no-layout' : ''}`}
              rowHeight={30}
              cols={12}
              height={size.height}
              width={size.width}
              layout={layout}
              onLayoutChange={onLayoutChange}
              onDrop={onDrop}
              droppingItem={{
                i: '__droppingItem__',
                w: 6,
                h: 10
              }}
              compactType="vertical"
              measureBeforeMount
              useCSSTransforms={false}
              draggableCancel=".nonDraggable"
              preventCollision={false}
              isDroppable={allowDragDrop}
              isDraggable={allowDragDrop}
              isResizable={allowDragDrop}
            >
              {layout.map((l, i) => (
                <El
                  key={l.i}
                  isExport={isExport}
                  className="visibilityContainer brTransparent"
                >
                  <SizeMe
                    monitorHeight
                    monitorWidth
                    resizeDetectorStrategy="object"
                  >
                    {({ size: childSize }) => (
                      <div
                        className={`chart-card white-to-grey-bg chart-card${i} fh`}
                      >
                        <div
                          style={
                            l?.chart?.defaultChartType === SUMMARY_TABLE
                              ? {
                                  height: isExport ? 'auto' : childSize.height,
                                  width: childSize.width,
                                  overflow: 'auto'
                                }
                              : {
                                  height: childSize.height,
                                  width: childSize.width
                                }
                          }
                        >
                          <GridHeader
                            setOpenChart={setOpenChart}
                            chart={l?.chart}
                            isExport={isExport}
                            isViewOnly={isViewOnly}
                            reportLayout={layout}
                            showDelete={!isViewOnly && !isExport}
                            removeElement={() => removeElementFromLayout(l)}
                            dispatch={dispatch}
                            isTvDashboard={reportsContext?.isTvDashboard}
                          />

                          <div className="chart_grid px-20">
                            <RenderChart
                              item={l}
                              size={{
                                width: childSize.width - 40,
                                height: childSize.height - 100
                              }}
                              numberChartCallback={(chartData) => {
                                !reportsContext?.isTvDashboard &&
                                  setSelectedChart(chartData);
                              }}
                              numberChartTooltip={getTranslatedText(
                                intl,
                                'common.view.data'
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </SizeMe>
                </El>
              ))}
            </ResponsiveReactGridLayout>
          )}
        </SizeMe>
      </Layout>
      {reportsContext.showChartsLibrary && (
        <ChartLibrary
          show={reportsContext.showChartsLibrary}
          selectedCharts={reportsContext?.activeReport?.charts || []}
          onDrag={setSelectedElement}
        />
      )}
      {!isEmpty(openChart) && (
        <ChartHome
          mode="edit"
          callBack={() => {
            setOpenChart(false);
          }}
          chart={openChart}
          removeChart={removeElementFromLayout}
          hideDelete={isViewOnly}
          source="report"
          layoutRef={layoutRef}
        />
      )}
      {/* Data table view of the chart */}
      {selectedChart.data != null &&
        !reportsContext?.isTvDashboard &&
        !isDataTableHidden(selectedChart) && (
          <Modal
            large
            open
            onClose={() => setSelectedChart({ data: null, index: 0 })}
          >
            <ChartContextProvider
              value={{
                value: selectedChart?.data,
                isEdit: true,
                metricIndex: selectedChart?.index
              }}
            >
              <DataTable
                showExportButton={selectedChart?.data?.chartId}
                tableHeader={selectedChart?.data?.name}
                isTableOpen={true}
                isTableEditable={false}
                disableToggle={true}
                isFromWidgetView
              />
            </ChartContextProvider>
          </Modal>
        )}
    </BuilderWrapper>
  );
}

export default withErrorBoundary(NewReportGrid);
